import React, { useEffect, useMemo, useState } from "react";
import { kioskAPI } from "../services/api";
import { getKioskPayload, kioskStorageKey } from "../utils/kiosk";
import "./KioskPage.css";

const DEFAULT_OBJECTIVE_ROTATION_SECONDS = 30;
const MIN_OBJECTIVE_ROTATION_SECONDS = 5;
const MAX_OBJECTIVE_ROTATION_SECONDS = 300;
const SLIDE_TRANSITION_MS = 700;

const formatCountdown = (secondsValue) => {
  const safeSeconds = Number.isFinite(secondsValue) ? Math.max(0, Math.floor(secondsValue)) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const chunkEntries = (entries, size = 2) => {
  const chunkSize = Number(size) > 0 ? Number(size) : 2;
  const result = [];
  for (let index = 0; index < entries.length; index += chunkSize) {
    result.push(entries.slice(index, index + chunkSize));
  }
  return result;
};

const splitByRepeatedTeksMarkers = (statementValue) => {
  const normalized = String(statementValue || "").replace(/\s+/g, " ").trim();
  if (!normalized) return [];
  const markerRe = /\b\d+\.\d+[A-Z](?:\/\d+\.\d+[A-Z])*\b/g;
  const matches = [...normalized.matchAll(markerRe)];
  if (matches.length < 2) return [normalized];

  const indices = matches
    .map((match) => match.index)
    .filter((index) => Number.isInteger(index))
    .sort((a, b) => a - b);

  const parts = [];
  for (let idx = 0; idx < indices.length; idx += 1) {
    const start = indices[idx];
    const end = idx + 1 < indices.length ? indices[idx + 1] : normalized.length;
    const segment = normalized.slice(start, end).trim();
    if (segment) parts.push(segment);
  }
  return parts.length ? parts : [normalized];
};

const splitStatementSegments = (statementValue) =>
  (() => {
    const normalized = String(statementValue || "").replace(/\s+/g, " ").trim();
    if (!normalized) return [];
    if (normalized.includes("|")) {
      return normalized
        .split("|")
        .map((segment) => segment.trim())
        .filter(Boolean);
    }
    return splitByRepeatedTeksMarkers(normalized);
  })();

const KioskPage = ({ channelKey }) => {
  const normalizedChannelKey = String(channelKey || "").trim().toLowerCase() || null;
  const localStorageKey = kioskStorageKey(normalizedChannelKey);
  const [payload, setPayload] = useState(() => getKioskPayload(normalizedChannelKey));
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [transitionPhase, setTransitionPhase] = useState("idle");
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      try {
        const response = normalizedChannelKey
          ? await kioskAPI.getPublishedPublic(normalizedChannelKey)
          : await kioskAPI.getPublished();
        if (cancelled) return;
        if (response.data) {
          setPayload(response.data);
          return;
        }
      } catch (_error) {
        // Fall back to local kiosk state when API is unavailable.
      }
      if (!cancelled) setPayload(getKioskPayload(normalizedChannelKey));
    };

    sync();
    const intervalId = window.setInterval(sync, 5000);
    const onStorage = (event) => {
      if (event.key === localStorageKey) sync();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("storage", onStorage);
    };
  }, [localStorageKey, normalizedChannelKey]);

  const entries = useMemo(
    () => (Array.isArray(payload?.entries) ? payload.entries.filter(Boolean) : []),
    [payload?.entries],
  );
  const entryPages = useMemo(() => chunkEntries(entries, 2), [entries]);
  const hasMultiplePages = entryPages.length > 1;
  const objectiveRotationSeconds = useMemo(() => {
    const parsed = Number(payload?.rotationSeconds);
    if (!Number.isInteger(parsed)) return DEFAULT_OBJECTIVE_ROTATION_SECONDS;
    if (
      parsed < MIN_OBJECTIVE_ROTATION_SECONDS
      || parsed > MAX_OBJECTIVE_ROTATION_SECONDS
    ) {
      return DEFAULT_OBJECTIVE_ROTATION_SECONDS;
    }
    return parsed;
  }, [payload?.rotationSeconds]);
  const objectiveRotationMs = objectiveRotationSeconds * 1000;

  useEffect(() => {
    if (!entryPages.length) {
      setActivePageIndex(0);
      return;
    }
    if (activePageIndex >= entryPages.length) {
      setActivePageIndex(0);
    }
  }, [activePageIndex, entryPages.length]);

  useEffect(() => {
    if (!hasMultiplePages) {
      setTransitionPhase("idle");
      setSecondsRemaining(0);
      return undefined;
    }

    const nextSwitchAt = Date.now() + objectiveRotationMs;
    const nextPageIndex = (activePageIndex + 1) % entryPages.length;
    let cancelled = false;
    let swapTimeoutId = null;
    let settleTimeoutId = null;

    const updateCountdown = () => {
      const msRemaining = Math.max(0, nextSwitchAt - Date.now());
      setSecondsRemaining(Math.ceil(msRemaining / 1000));
    };

    updateCountdown();

    const countdownIntervalId = window.setInterval(updateCountdown, 250);
    const rotationTimeoutId = window.setTimeout(() => {
      setTransitionPhase("out");
      swapTimeoutId = window.setTimeout(() => {
        if (cancelled) return;
        setActivePageIndex(nextPageIndex);
        setTransitionPhase("in");
        settleTimeoutId = window.setTimeout(() => {
          if (cancelled) return;
          setTransitionPhase("idle");
        }, SLIDE_TRANSITION_MS);
      }, SLIDE_TRANSITION_MS);
    }, objectiveRotationMs);

    return () => {
      cancelled = true;
      window.clearInterval(countdownIntervalId);
      window.clearTimeout(rotationTimeoutId);
      if (swapTimeoutId) window.clearTimeout(swapTimeoutId);
      if (settleTimeoutId) window.clearTimeout(settleTimeoutId);
    };
  }, [activePageIndex, entryPages.length, hasMultiplePages, objectiveRotationMs]);

  if (!payload) {
    return (
      <div className="kiosk-display kiosk-empty">
        <h1>Kiosk Display</h1>
        {normalizedChannelKey ? (
          <p>No learning objective published yet for this kiosk channel.</p>
        ) : (
          <p>No learning objective published yet.</p>
        )}
        {normalizedChannelKey ? (
          <p>Open your Kiosk Control and publish to this channel.</p>
        ) : (
          <p>Use the teacher channel URL format: /#/kiosk/your-channel-key.</p>
        )}
      </div>
    );
  }

  const activePageEntries = entryPages[activePageIndex] || [];
  const transitionClass =
    transitionPhase === "out"
      ? "is-exiting"
      : transitionPhase === "in"
        ? "is-entering"
        : "";

  return (
    <div className="kiosk-display">
      <header className="kiosk-header">
        <div>
          <h1>{payload.displayTitle || "Learning Objective Display"}</h1>
          <p>
            Updated{" "}
            {payload.updatedAt ? new Date(payload.updatedAt).toLocaleString() : "recently"}
          </p>
        </div>
        {hasMultiplePages ? (
          <div className="kiosk-rotation-timer" aria-live="polite">
            <span className="kiosk-rotation-label">Next objective page in</span>
            <strong>{formatCountdown(secondsRemaining)}</strong>
          </div>
        ) : null}
      </header>

      <main className="kiosk-content">
        {!activePageEntries.length ? (
          <section className="kiosk-objective-block">
            <div className="kiosk-statement">No objectives are currently published for this day.</div>
          </section>
        ) : (
          <div className="kiosk-slide-stage">
            <div
              key={`page-${activePageIndex}`}
              className={`kiosk-slide-panel ${transitionClass}`.trim()}
            >
              <div className="kiosk-objective-grid">
                {activePageEntries.map((entry, entryIdx) => (
                  <section
                    key={`${entry?.teksCode || "teks"}-${activePageIndex}-${entryIdx}`}
                    className="kiosk-objective-block kiosk-objective-card"
                  >
                    <div className="kiosk-teks">{entry?.teksCode || "TEKS"}</div>
                    {(() => {
                      const statementSegments = splitStatementSegments(
                        entry?.statement || "The student will demonstrate mastery of the standard.",
                      );
                      if (statementSegments.length <= 1) {
                        return (
                          <div className="kiosk-statement">
                            {statementSegments[0] || "The student will demonstrate mastery of the standard."}
                          </div>
                        );
                      }
                      return (
                        <div className="kiosk-statement-list" role="list" aria-label="Objective statements">
                          {statementSegments.map((segment, segmentIdx) => (
                            <div
                              key={`${activePageIndex}-${entryIdx}-segment-${segmentIdx}`}
                              className="kiosk-statement-item"
                              role="listitem"
                            >
                              <span className="kiosk-statement-index">{segmentIdx + 1}</span>
                              <p>{segment}</p>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    {entry?.imageUrl ? (
                      <img
                        className="kiosk-objective-image"
                        src={entry.imageUrl}
                        alt={`Objective visual ${activePageIndex + 1}-${entryIdx + 1}`}
                      />
                    ) : null}
                    <ul>
                      {(entry?.iCanStatements || [])
                        .filter((line) => String(line || "").trim())
                        .map((line, lineIdx) => (
                          <li key={`${activePageIndex}-${entryIdx}-${lineIdx}`}>{line}</li>
                        ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default KioskPage;
